import * as React from 'react'
import { ArrowLeft, ArrowRight, Coins, Timer, Trophy, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  COINS_PER_TEAM,
  coinsCollected,
  landingFrom,
  MAX_BOX,
  MAX_ON_TRACK,
  MIN_BOX,
  ROUND_SECONDS,
  roundScore,
  TRACK,
  WELCOME_SECONDS,
} from '@/lib/sheep-race'

/**
 * How to play Sheep Race, in seven screens.
 *
 * A mock board rather than a live one: the whole point is to show a roll that
 * went a particular way, and a real race would roll something else. Every
 * number on the worked-example screens comes from `roundScore()` — the same
 * function the page uses and `sheep-race-check.mjs` asserts against the two
 * examples the rules were written with — so the tutorial cannot drift from the
 * game while still looking like it agrees.
 */

/** A still of the track: coins where you put them, the sheep where it stopped. */
function MiniTrack({
  side = 'a',
  coins = [],
  position,
  recovered = [],
  label,
}: {
  side?: 'a' | 'b'
  /** Boxes holding one of this side's coins. Repeat a box for a stack. */
  coins?: number[]
  /** Where the sheep finished, or undefined to leave it in the pen. */
  position?: number
  /** Boxes whose coin came back. Drawn differently from one that did not. */
  recovered?: number[]
  label?: string
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      {label && <p className="mb-2 text-xs font-medium">{label}</p>}
      <div className="flex min-w-max items-end gap-1 overflow-x-auto">
        <div className="relative mr-1 h-12 w-12 shrink-0">
          {position === undefined && (
            <span
              className="sheep sheep-sm sheep-right absolute bottom-0 left-0 block"
              style={{
                transform: 'scale(0.7)',
                transformOrigin: 'bottom left',
                filter: side === 'b' ? 'grayscale(1) brightness(0.72) contrast(1.1)' : undefined,
              }}
            />
          )}
        </div>
        {TRACK.map((box) => {
          const here = coins.filter((c) => c === box).length
          const back = recovered.includes(box)
          return (
            <div
              key={box}
              className={cn(
                'relative flex h-12 w-8 shrink-0 flex-col items-center justify-end rounded border text-[10px]',
                position === box ? 'border-primary bg-primary/15' : 'border-dashed',
              )}
            >
              {position === box && (
                <span
                  className="sheep sheep-sm sheep-right absolute -top-3 left-1/2 block"
                  style={{
                    transform: 'translateX(-50%) scale(0.5)',
                    transformOrigin: 'bottom center',
                    filter: side === 'b' ? 'grayscale(1) brightness(0.72) contrast(1.1)' : undefined,
                  }}
                />
              )}
              {here > 0 && (
                <span
                  className={cn(
                    'z-10 mb-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                    back
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                      : 'bg-amber-400 text-amber-950',
                  )}
                >
                  {here}
                </span>
              )}
              <span className="mb-0.5 text-muted-foreground tabular-nums">{box}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** One line of the arithmetic, with the total called out. */
function Sum({
  label,
  kept,
  back,
  position,
}: {
  label: string
  kept: number
  back: number
  position: number
}) {
  const total = roundScore({ placed: COINS_PER_TEAM - kept, recovered: back, position })
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm tabular-nums">
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">kept</span>
      <span className="font-semibold">{kept}</span>
      <span className="text-muted-foreground">+ back</span>
      <span className="font-semibold">{back}</span>
      <span className="text-muted-foreground">− short</span>
      <span className="font-semibold">{MAX_BOX - position}</span>
      <span className="text-muted-foreground">=</span>
      <span className="rounded bg-primary/10 px-1.5 font-bold text-primary">{total}</span>
    </div>
  )
}

/**
 * The two worked examples, derived rather than typed.
 *
 * `coinsCollected` and `landingFrom` are the same functions the game's rules
 * live in, so a change to how collection works shows up here as a changed
 * tutorial rather than as a tutorial that now lies. `sheep-race-check.mjs`
 * asserts both examples against `roundScore`.
 */
const example = (coins: number[], reach: number) => {
  const back = coinsCollected(coins, reach)
  return { coins, reach, back, position: landingFrom(reach, back.length) }
}
const WHITE = example([6, 7, 8], 7)
const GREY = example([4, 5, 6, 7], 9)
const score = (e: ReturnType<typeof example>) =>
  roundScore({ placed: e.coins.length, recovered: e.back.length, position: e.position })
const whiteScore = score(WHITE)
const greyScore = score(GREY)

/** Chance two dice reach a box at all — 36ths, rounded. */
const REACH = [
  { box: '2–6', pct: '83–100%', note: 'worth it' },
  { box: '7', pct: '58%', note: 'the frontier' },
  { box: '8', pct: '42%', note: 'a gamble' },
  { box: '9', pct: '28%', note: 'a gamble' },
  { box: '10–12', pct: '3–17%', note: 'a prayer' },
]

interface Screen {
  title: string
  body: React.ReactNode
}

const SCREENS: Screen[] = [
  {
    title: 'Five rounds, and the most coins takes each one',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          Everyone on the team is dealt onto one of two sides, White or Grey, each with a sheep of
          its own. You play five rounds; whoever takes the most of them wins the race.
        </p>
        <p>
          A round is not won by the sheep that gets furthest. It is won by the side that{' '}
          <span className="font-medium">ends the round holding more coins</span> — which usually
          means getting far, but not always, because getting far costs coins.
        </p>
        <p className="rounded-md border-l-4 border-primary/40 bg-primary/5 px-3 py-2 text-xs">
          A round where both sides score the same is void and played again, so every round has a
          winner.
        </p>
      </div>
    ),
  },
  {
    title: 'Your side has eight coins, and may place seven',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          The eight belong to the side, not to you — anybody on it can place one and anybody can
          take one back. You may put at most {MAX_ON_TRACK} of them on the track; the eighth stays
          in hand whatever you do.
        </p>
        <MiniTrack coins={[6, 7, 7, 9]} label="Four coins down, two of them stacked on 7" />
        <p>
          Two coins on one box is allowed, and counts twice. The track runs {MIN_BOX} to {MAX_BOX}{' '}
          because that is what two dice can make — there is no box 1.
        </p>
      </div>
    ),
  },
  {
    title: 'What a coin does',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          Each sheep rolls two dice and runs that many boxes. It picks up{' '}
          <span className="font-medium">every one of your coins it runs over</span> on the way — not
          just the one it stops on. Each coin it collects:
        </p>
        <ul className="ml-4 list-disc space-y-1.5">
          <li>
            <span className="font-medium">comes back to you</span>, and
          </li>
          <li>
            <span className="font-medium">carries the sheep one box further</span>.
          </li>
        </ul>
        <p>
          So a line of coins is a run: the dice sweep them all up at once. A coin out beyond where
          the dice reached is simply spent.
        </p>
        <p className="rounded-md border-l-4 border-muted-foreground/30 bg-muted/40 px-3 py-2 text-xs">
          The sheep collects what the <span className="font-medium">dice</span> ran it over. Being
          pushed past another of your coins does not pick that one up too — otherwise one cheap coin
          low down would drag the whole row in behind it.
        </p>
      </div>
    ),
  },
  {
    title: 'The score',
    body: (
      <div className="space-y-3 text-sm">
        <p>At the end of a round each side counts:</p>
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-center text-sm font-medium">
          coins kept + coins recovered − boxes short of {MAX_BOX}
        </div>
        <p>
          Place nothing and finish on {MAX_BOX} and you score all {COINS_PER_TEAM}. Place nothing
          and finish on 8 and you score {roundScore({ placed: 0, recovered: 0, position: 8 })} —
          four boxes short is four coins gone.
        </p>
        <p>
          That is the squeeze. Coins buy you distance, distance is what stops the coins being taken
          off you again, and the sheep that wins the race is not always the side that scores more.
        </p>
      </div>
    ),
  },
  {
    title: 'A worked round',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          White puts three coins out, on 6, 7 and 8. The dice come up 7, so the sheep runs over the
          6 and the 7 and collects both — two coins back, two boxes further, finishing on 9. The
          coin on 8 is past where the dice reached, so it stays there.
        </p>
        <MiniTrack
          coins={WHITE.coins}
          position={WHITE.position}
          recovered={WHITE.back}
          label={`White — dice ${WHITE.reach}`}
        />
        <Sum label="White" kept={COINS_PER_TEAM - WHITE.coins.length} back={WHITE.back.length} position={WHITE.position} />
        <p className="pt-1">
          Grey puts four out, low, on 4, 5, 6 and 7, and rolls a 9 — over the lot. All four come
          back and shove the sheep from 9 to the end of the track.
        </p>
        <MiniTrack
          side="b"
          coins={GREY.coins}
          position={GREY.position}
          recovered={GREY.back}
          label={`Grey — dice ${GREY.reach}`}
        />
        <Sum label="Grey" kept={COINS_PER_TEAM - GREY.coins.length} back={GREY.back.length} position={GREY.position} />
        <p className="pt-1 font-medium">
          Grey takes the round, {greyScore} to {whiteScore}. The coins did it, not the dice — a 9
          beats a 7 by two boxes, and the run was worth eight.
        </p>
      </div>
    ),
  },
  {
    title: 'The clock does the rolling',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          There is no roll button. The dice land every{' '}
          <span className="font-medium">{ROUND_SECONDS} seconds</span>, and the first round gets{' '}
          {WELCOME_SECONDS} more so the room has time to look at the board.
        </p>
        <p>
          Coins placed after the clock runs out are refused — if your side put nothing down, it
          simply races with all eight in hand, which is a perfectly respectable bet on a slow
          sheep.
        </p>
        <p className="rounded-md border-l-4 border-primary/40 bg-primary/5 px-3 py-2 text-xs">
          You cannot see the other side's coins until the dice land. They cannot see yours either.
        </p>
      </div>
    ),
  },
  {
    title: 'Where the argument is',
    body: (
      <div className="space-y-3 text-sm">
        <p>
          A coin costs you one and pays two — it comes back and it moves you. So it is worth placing
          wherever your sheep <span className="font-medium">reaches</span> that box more often than
          not, and two dice reach a low box almost always:
        </p>
        <table className="w-full text-xs tabular-nums">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 text-left font-medium">Coin on</th>
              <th className="py-1 text-left font-medium">Sheep reaches it</th>
              <th className="py-1 text-left font-medium" />
            </tr>
          </thead>
          <tbody>
            {REACH.map((r) => (
              <tr key={r.box} className="border-t">
                <td className="py-1 font-semibold">{r.box}</td>
                <td className="py-1">{r.pct}</td>
                <td className="py-1 text-muted-foreground">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          Which makes the low boxes look free, and they are not. Every coin you place is a coin you
          are not holding if the dice come up short — eight in hand scores between −2 and 8 and
          averages 3, while seven coins spread across the low boxes averages nearly 5 and can lose
          you 7.
        </p>
        <p>
          So the argument is never "is this box good". It is how much of the round your side wants
          to put at risk, with four rounds still to play and the other sheep hidden until the dice
          land.
        </p>
      </div>
    ),
  },
]

export function SheepTutorial({ onClose }: { onClose: () => void }) {
  const [step, setStep] = React.useState(0)
  const screen = SCREENS[step]
  const last = step === SCREENS.length - 1

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            {step === 0 && <Trophy className="h-4 w-4 shrink-0 text-primary" />}
            {step === 5 && <Timer className="h-4 w-4 shrink-0 text-primary" />}
            {step > 0 && step < 5 && <Coins className="h-4 w-4 shrink-0 text-primary" />}
            {screen.title}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">{screen.body}</div>

        <div className="flex items-center gap-2 border-t pt-3">
          <div className="flex gap-1.5">
            {SCREENS.map((s, i) => (
              <button
                key={s.title}
                type="button"
                onClick={() => setStep(i)}
                aria-label={`Screen ${i + 1}: ${s.title}`}
                aria-current={i === step}
                className={cn(
                  'h-1.5 w-6 rounded-full transition-colors',
                  i === step ? 'bg-primary' : 'bg-muted hover:bg-muted-foreground/40',
                )}
              />
            ))}
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">
            {step + 1} / {SCREENS.length}
          </span>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
            >
              <ArrowLeft /> Back
            </Button>
            {last ? (
              <Button size="sm" onClick={onClose}>
                <X /> Close
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep((s) => s + 1)}>
                Next <ArrowRight />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
